import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { Newsletter, NewsletterSummary } from '../types/models/Newsletter';
import type { ApiResponseNewsletterList } from '../types/api/ApiResponseNewsletter';

interface UseNewslettersReturn {
  newsletters: NewsletterSummary[];
  /** True while any fetch is in flight (initial load OR a view switch / manual refresh). A same-view
   *  refresh keeps the current rows on screen; a VIEW SWITCH clears them first (see below), so gate
   *  a full-panel spinner on `loading && newsletters.length === 0`. */
  loading: boolean;
  /** Human-readable error message (the backend's JSend `message`), or null. */
  error: string | null;
  refetch: () => Promise<void>;
  /**
   * Upsert a created/updated/sent newsletter into the CURRENT view (matched by `id`) so a mutation
   * updates the table without a full refetch. A row only belongs to the view whose archived state
   * it matches — an edit/send keeps it in place; a fresh create (always an active draft) is
   * prepended (newest-first) only while the Active view shows; a row that no longer belongs is
   * dropped (defensive).
   */
  applyNewsletter: (updated: Newsletter) => void;
  /** Drop one newsletter row (used after a hard delete, or after archiving from the Active view). */
  removeNewsletter: (id: number) => void;
}

/** Collapse a full newsletter (mutation response) into the lighter list-summary shape. */
export const newsletterToSummary = (full: Newsletter): NewsletterSummary => ({
  id: full.id,
  newsletter_template_id: full.newsletter_template_id,
  subject: full.subject,
  status: full.status,
  is_sent: full.is_sent,
  sent_at: full.sent_at,
  not_before: full.not_before,
  is_scheduled: full.is_scheduled,
  is_archived: full.is_archived,
  archived_at: full.archived_at,
  html_bytes: full.html === null ? 0 : new TextEncoder().encode(full.html).length,
  created_at: full.created_at,
  updated_at: full.updated_at,
});

/**
 * Fetches the newsletter list for the Ancalagon-staff-only Newsletter admin page
 * (`GET /admin/newsletters`). The `archived` flag selects the server-side set — active by default,
 * or the archived set with `?archived=1` — so toggling it refetches. Rows come back newest-first as
 * lightweight summaries (no `content`/`html`).
 *
 * The endpoint returns the whole set in one call (no server-side search/pagination params), so the
 * consumer searches and paginates client-side. Per the API contract the backend wraps responses in
 * JSend and maps failures to `status: 'error'` at HTTP 200, so this branches on
 * `body.status === 'success'`, never `response.ok`.
 */
export const useNewsletters = (archived: boolean): UseNewslettersReturn => {
  const [newsletters, setNewsletters] = useState<NewsletterSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchNewsletters = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`/admin/newsletters${archived ? '?archived=1' : ''}`);
      // Branch on the JSend `body.status`, NEVER `response.ok`: the backend maps controller
      // exceptions to `status: 'error'` at HTTP 200, so `response.ok` can be true on a failure.
      const body = (await response.json().catch(() => null)) as ApiResponseNewsletterList | null;
      if (body?.status === 'success' && Array.isArray(body.data?.newsletters)) {
        setNewsletters(body.data.newsletters);
      } else {
        setError(body?.message ?? 'Failed to load newsletters.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching newsletters.');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, archived]);

  const applyNewsletter = useCallback(
    (updated: Newsletter) => {
      const summary = newsletterToSummary(updated);
      setNewsletters((prev) => {
        const belongs = updated.is_archived === archived;
        const exists = prev.some((n) => n.id === updated.id);
        if (belongs) {
          return exists ? prev.map((n) => (n.id === updated.id ? summary : n)) : [summary, ...prev];
        }
        return exists ? prev.filter((n) => n.id !== updated.id) : prev;
      });
    },
    [archived],
  );

  const removeNewsletter = useCallback((id: number) => {
    setNewsletters((prev) => prev.filter((n) => n.id !== id));
  }, []);

  // Switching the Active/Archived set makes the currently-held rows belong to the OTHER view — drop
  // them immediately so a failed (or in-flight) switch never shows the previous view's rows under
  // the new label. A same-view refresh doesn't change `archived`, so its rows are kept on screen.
  useEffect(() => {
    setNewsletters([]);
  }, [archived]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      void fetchNewsletters();
    }
  }, [auth.isAuthenticated, fetchNewsletters]);

  return {
    newsletters,
    loading,
    error,
    refetch: fetchNewsletters,
    applyNewsletter,
    removeNewsletter,
  };
};

export default useNewsletters;
